import React from 'react';
import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useSelector } from 'react-redux';
import { ACCESS_DENIED_MESSAGES } from '../constants/accessDenied';

const ICON_BG_ALPHA = 0.2;
const OUTER_RING_ALPHA = 0.12;

export default function AccessDeniedCard({ title }) {
  const theme = useTheme();
  const rootUserEmail = useSelector((state) => state.org.org?.root_user_email ?? '');
  const email = rootUserEmail.trim();

  const headline = ACCESS_DENIED_MESSAGES.headline_with_module.replace('{module}', title || 'this page');
  const description = ACCESS_DENIED_MESSAGES.description;
  const contactAdminLabel = ACCESS_DENIED_MESSAGES.contact_admin;

  const primarySoft = alpha(theme.palette.primary.main, OUTER_RING_ALPHA);
  const primaryIconBg = alpha(theme.palette.primary.main, ICON_BG_ALPHA);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        width: '100%',
        px: 2,
        py: 4,
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${theme.palette.background.default} 45%)`,
      }}
    >
      <Stack
        spacing={3}
        alignItems="center"
        sx={{
          width: '100%',
          maxWidth: 560,
          textAlign: 'center',
        }}
      >
        <Fade in timeout={600}>
          <Box
            sx={{
              position: 'relative',
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: primarySoft,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  bgcolor: primarySoft,
                  animation: 'accessDeniedPulse 3s ease-in-out infinite',
                },
                '@keyframes accessDeniedPulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 0.6,
                  },
                  '50%': {
                    transform: 'scale(1.08)',
                    opacity: 0.3,
                  },
                  '100%': {
                    transform: 'scale(1.15)',
                    opacity: 0,
                  },
                },
              }}
            />
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: 96,
                height: 96,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: primaryIconBg,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 44, color: 'primary.light' }} aria-hidden />
            </Box>
          </Box>
        </Fade>

        <Fade in timeout={800}>
          <Stack spacing={3} alignItems="center" sx={{ width: '100%' }}>
            <Stack spacing={1.5} sx={{ maxWidth: 440 }}>
              <Typography variant="h5" component="h1" fontWeight={600} color="text.primary">
                {headline}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {description}
              </Typography>
            </Stack>

            {email ? (
              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" color="primary" component="a" href={`mailto:${email}`}>
                  {contactAdminLabel}
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Fade>
      </Stack>
    </Box>
  );
}
